import { STATUS } from "@/constants/statuses";
import { BehaviorSubject, map, mergeAll, of, switchMap, tap } from "rxjs";

import UploadedFileEntity from "./entitites/UploadedFileEntity";
import FileUploaderService from "./FileUploaderService";
import {
    FileValidatorInterface,
    ValidationReturnValue,
} from "./validators/FileValidatorInterface";

export type FileType = {
    name: string;
    url: string;
    status: STATUS;
    error: string | null;
    key: string | null;
    bucket: string | null;
};

type FileUploaderOptions = {
    maxNumberOfFiles?: number | undefined;
    appName: string;
};

export default class FileUploaderManager {
    public validationErrors$ = new BehaviorSubject<string[]>([]);
    public files$ = new BehaviorSubject<FileType[]>([]);
    public uploadedFiles$ = this.files$.pipe(
        map((files) => files.filter((file) => file.status === STATUS.READY))
    );

    constructor(
        private readonly validator: FileValidatorInterface,
        private readonly fileUploaderService: FileUploaderService,
        private readonly options: FileUploaderOptions = { appName: "" }
    ) {}

    addFiles(newFiles: File[]): void {
        const { maxNumberOfFiles } = this.options;
        const numberOfFilesValid =
            maxNumberOfFiles &&
            this.files$.value.length + newFiles.length > maxNumberOfFiles;

        if (!newFiles.length) {
            return;
        }

        if (numberOfFilesValid) {
            this.validationErrors$.next([
                `Only ${maxNumberOfFiles} files allowed to upload`,
            ]);
            return;
        } else {
            this.validationErrors$.next([]);
        }

        const newFiles$ = of(newFiles);

        const validatedFiles$ = newFiles$.pipe(
            map((files) => this.validator.validateFiles(files)),
            mergeAll()
        );

        validatedFiles$
            .pipe(
                map((validatedFiles) =>
                    validatedFiles.filter((item) => item.valid)
                ),
                this.addToPreview,
                switchMap((validatedFiles) =>
                    this.fileUploaderService.upload(
                        validatedFiles.map(
                            (validatedFiles) => validatedFiles.file
                        ),
                        this.options.appName
                    )
                ),
                this.stopLoaders
            )
            .subscribe();

        validatedFiles$
            .pipe(
                map((validatedFiles) =>
                    validatedFiles
                        .filter((item) => !item.valid)
                        .map(
                            (item) =>
                                `"${item.file.name}": ${
                                    item.error || "Is not valid"
                                }`
                        )
                )
            )
            .subscribe((values) => {
                this.validationErrors$.next(values);
            });
    }

    private addToPreview = tap<ValidationReturnValue[]>((validatedFiles) => {
        const previewFiles = validatedFiles.map<FileType>((validatedFile) => {
            const url = URL.createObjectURL(validatedFile.file);

            return {
                name: validatedFile.file.name,
                url,
                status: STATUS.LOADING,
                error: null,
                key: null,
                bucket: null,
            };
        });

        this.files$.next([...this.files$.value, ...previewFiles]);
    });

    private stopLoaders = tap<UploadedFileEntity[]>((uploadedFiles) => {
        this.files$.next(
            this.files$.value.map((item) => {
                const uploadedFile = uploadedFiles.find(
                    (uf) => uf.originFileName == item.name
                );

                if (uploadedFile) {
                    return {
                        ...item,
                        status: STATUS.READY,
                        key: uploadedFile?.key,
                        bucket: uploadedFile.bucket,
                    };
                }

                return {
                    ...item,
                };
            })
        );
    });

    removeFile(fileName: string) {
        this.files$.next(
            this.files$.value.filter((item) => item.name !== fileName)
        );
    }

    resetFiles() {
        this.files$.next([]);
    }
}
